/**
 * 加密请求工具（与 Go 服务端 AuthToken 解密逻辑对齐）
 *
 * 服务端解密步骤：
 *   token(header) -> json 解析出 {p,k,iv,v}
 *   k  -> base64 decode -> RSA(私钥, PKCS1v1.5) 解密 -> AES Key
 *   iv -> base64 decode -> RSA(私钥, PKCS1v1.5) 解密 -> hex 字符串 -> hex decode -> IV
 *   p  -> base64 decode -> AES-CBC(PKCS7) 解密 -> body 明文 sha256 哈希（hex 字符串）
 *   最后校验：解密得到的哈希 == sha256(body)
 *
 * 前端加密步骤（与测试脚本 auth_crypto.py 一一对应）：
 *   1. 生成随机 AES Key(32B) + IV(16B)
 *   2. 计算 body 明文 sha256 哈希(hex) -> AES-CBC + PKCS7 加密 -> p（base64）
 *   3. RSA(PKCS1v1.5) 加密 AES Key -> k（base64）
 *   4. RSA(PKCS1v1.5) 加密 IV 的 hex 字符串 -> iv（base64）
 *   5. 当前时间戳(秒) -> v
 *   6. 组装 {p,k,iv,v} JSON 作为 header token
 *
 * 注意：p 中加密的是 body 明文的 sha256 哈希（哈希对象为明文的 UTF-8 字节），
 * 服务端解密后直接与 sha256(body) 对比即可，无需再恢复明文。
 *
 * RSA 公钥来源：七彩石配置中心 getConfigItem('exDcopsToken')。
 * 注意：七彩石配置在路由守卫里异步拉取，因此必须每次构建 token 时实时读取 store，
 * 不能缓存为模块加载时的值（否则配置到达前永远拿不到公钥）。
 * 且 store 与 request 存在循环引用，只能在函数体内（运行时）访问 store.getters。
 */
import forge from "node-forge";
import store from "@/store";
import { getServerTimestampSec } from "./clockSync";

/**
 * 规范化七彩石配置中的公钥内容为可解析的 PEM 字符串
 * @param {string} content 七彩石配置原文
 * @returns {string} PEM 格式公钥
 */
function normalizePublicKeyPem(content) {
  let pem = String(content).trim();
  // 配置经 JSON 存储时 \n 可能被转义为字面 "\\n"
  pem = pem.replace(/\\n/g, "\n");
  // 已是标准 PEM（带 BEGIN/END PUBLIC KEY 标记），直接使用
  if (/BEGIN PUBLIC KEY/.test(pem)) return pem;
  // 裸 base64：补齐 PEM 外壳（默认 PKCS#8 / SubjectPublicKeyInfo 格式）
  return `-----BEGIN PUBLIC KEY-----\n${pem}\n-----END PUBLIC KEY-----`;
}

/**
 * 从七彩石配置获取 RSA 公钥（PEM）
 * @returns {Promise<string>} PEM 公钥
 */
async function getPublicKey() {
  const content = store.getters.getConfigItem("exDcopsToken");
  if (!content) {
    throw new Error("七彩石配置 exDcopsToken 为空，无法生成加密 token");
  }
  return normalizePublicKeyPem(content);
}

/**
 * 生成 header token：{p,k,iv,v} 的 JSON 字符串
 * @param {string} plaintext body 明文（UTF-8 字符串，必须与最终发送的 body 字节完全一致）
 * @returns {Promise<string>}
 */
export async function buildAuthToken(plaintext) {
  const pem = await getPublicKey();
  const publicKey = forge.pki.publicKeyFromPem(pem);

  // 1. 随机 AES Key(32B) + IV(16B)
  const aesKey = forge.random.getBytesSync(32);
  const iv = forge.random.getBytesSync(16);

  // 2. 计算 body 明文的 sha256 哈希(hex) -> AES-CBC(PKCS7) 加密 -> p（base64）
  //    哈希对象为 body 明文的 UTF-8 字节（对齐 Python hashlib.sha256(明文.encode("utf-8"))）
  //    forge 的 AES-CBC 默认使用 PKCS7 填充，与 Python PKCS7 padder 一致
  const md = forge.md.sha256.create();
  md.update(forge.util.encodeUtf8(plaintext));
  const digestHex = md.digest().toHex();
  const cipher = forge.cipher.createCipher("AES-CBC", forge.util.createBuffer(aesKey));
  cipher.start({ iv: forge.util.createBuffer(iv) });
  cipher.update(forge.util.createBuffer(digestHex)); // digestHex 为 ASCII，raw 读入与 UTF-8 编码等价
  cipher.finish();
  const payloadB64 = forge.util.encode64(cipher.output.getBytes());

  // 3. RSA(PKCS1v1.5) 加密 AES Key -> k（base64）
  const keyB64 = forge.util.encode64(publicKey.encrypt(aesKey, "RSAES-PKCS1-V1_5"));

  // 4. RSA(PKCS1v1.5) 加密 IV 的 hex 字符串 -> iv（base64）
  //    服务端 RSA 解密得到 hex 字符串，再 hex.DecodeString 还原 IV
  const ivHex = forge.util.bytesToHex(iv);
  const ivB64 = forge.util.encode64(
    publicKey.encrypt(forge.util.encodeUtf8(ivHex), "RSAES-PKCS1-V1_5")
  );

  // 5. 时间戳(秒) -> v（服务端 ParseInt 解析，校验 ±60 秒）
  //    使用「服务器时间」而非本机时间：手机系统时间不准时也能通过校验，
  //    偏移量由 clockSync 从响应 Date 头自动校准，不再需要用户手动改手机时间
  const version = String(getServerTimestampSec());

  return JSON.stringify({ p: payloadB64, k: keyB64, iv: ivB64, v: version });
}

/**
 * 请求体是否能用加密 token 对齐。
 * FormData/Blob/URLSearchParams 等无法在发送前复现最终 body 字节，
 * 无法保证 sha256(明文) == sha256(body)，返回 false 则跳过加 token。
 */
export function isEncryptableBody(data) {
  if (data == null) return true; // 无 body（GET 等），明文为空串
  if (typeof data === "object") {
    if (data instanceof FormData) return false;
    if (data instanceof Blob) return false;
    if (data instanceof URLSearchParams) return false;
  }
  return true;
}

/**
 * 把请求体序列化为 body 明文（sha256 哈希即对这份明文计算）
 * 使用紧凑 JSON，与 Python json.dumps(data, separators=(",", ":"), ensure_ascii=False) 对齐
 * @param {*} data
 * @returns {string}
 */
export function serializeBody(data) {
  if (data == null) return ""; // undefined / null：明文为空串
  if (typeof data === "string") return data;
  const str = JSON.stringify(data);
  return str === undefined ? "" : str;
}
